/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable complexity */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { addressFormFieldsSX } from '@/components/blocks/AddressForm/styles/fields';
import {
	shippingTitleBookSX,
	shippingTitleGeneralSX,
} from '@/components/content/CheckOut/parts/Shipping/custom/styles/typography';
import { useLocalization } from '@/data/Localization';
import { EditableAddress } from '@/data/types/Address';
import { Country, State } from '@/data/types/CountryState';
import { REG_EX } from '@/utils/address';
import { ErrorState } from '@/utils/useForm';
import {
	AutocompleteInputChangeReason,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	Skeleton,
	Stack,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import {
	ChangeEvent,
	FC,
	SyntheticEvent,
	useState,
	useEffect,
	Fragment,
	useRef,
	useMemo,
} from 'react';
import { SelectChangeEvent } from '@mui/material';
import {
	AccountInformationBpCheckboxSX,
	AccountInformationFormCheckboxSX,
} from '@/components/content/Account/styles/accountInformationForm';
import ErrorIcon from '@mui/icons-material/Error';
import { formatPhoneNumber, formatPhoneNumberValue } from '@/utils/InputPhone';
import { BpCheckbox } from '@/components/content/Account/parts/InformationForm';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useCart } from '@/data/Content/Cart';
import { useExtendedZoneValidate } from '@/data/Content/ExtendedZone';

import { EMPTY_STRING } from '@/data/constants/marketing';
import { useZipCode } from '@/data/hooks/useZipCode';
import { AlertExtendedZone } from '@/components/blocks/OrderDetails/parts/AlertExtendedZone';
import { cleanTextFomEmojis } from '@/utils/cleanTextFomEmojis';

type Props = {
	nickNameDisabled: boolean;
	address: EditableAddress;
	handleAutoCompleteInputChange: (
		name: keyof EditableAddress
	) => (
		_event: SyntheticEvent<Element, Event>,
		value: string,
		_reason: AutocompleteInputChangeReason
	) => void;
	handleChange: (event: ChangeEvent<HTMLInputElement>) => void;
	selectChange: (event: SelectChangeEvent) => void;
	error: ErrorState<EditableAddress>;
	countries: Country[];
	states: State[];
	helperText: { [k: string]: string };
	showAddressType: boolean;
	formLabel?: JSX.Element;
	editPerson?: boolean;
	setSubmitting?: any;
	addressBook?: boolean;
};

export const AddressFormFields: FC<Props> = ({
	address,
	handleChange,
	selectChange,
	nickNameDisabled = false,
	editPerson,
	setSubmitting,
	addressBook,
}) => {
	const addressFormNLS = useLocalization('AddressForm');
	const { data: cart } = useCart();
	const { validateExtendedZone } = useExtendedZoneValidate();
	const CustomIconComponent = (props: any) => (
		<KeyboardArrowDownIcon
			{...props}
			sx={{ color: address.zipCode.length === 5 ? '#0467D2' : '#b5beca' }}
		/>
	);
	const [addressInfo, setAddressInfo] = useState<{
		city: string;
		state: string;
		neighborhood: string[];
	}>({
		city: '',
		state: '',
		neighborhood: [],
	});
	const [isExtendedZoneShipping, setIsExtendedZoneShipping] = useState(false);
	const [customNeighborhood, setCustomNeighborhood] = useState<boolean>(false);
	const [openSelect, setOpenSelect] = useState<boolean>(false);
	const [errorFirstName, setErrorFirstName] = useState<any>({ error: false, errorMsg: '' });
	const [errorLastName, setErrorLastName] = useState<any>({ error: false, errorMsg: '' });
	const [errorLastNameMat, setErrorLastNameMat] = useState<any>({ error: false, errorMsg: '' });
	const [errorPhone1, setErrorPhone1] = useState<any>({ error: false, errorMsg: '' });
	const [errorEmail1, setErrorEmail1] = useState<any>({ error: false, errorMsg: '' });
	const [errorNickname, setErrorNickName] = useState<any>({ error: false, errorMsg: '' });
	const [errorAddressLine1, setErrorAddressLine1] = useState<any>({ error: false, errorMsg: '' });
	const [errorXaddr_numExt, setErrorXaddr_numExt] = useState<any>({ error: false, errorMsg: '' });
	const [errorZipCode, setErrorZipCode] = useState<any>({ error: false, errorMsg: '' });
	const [errorAddressLine2, setErrorAddressLine2] = useState<any>({ error: false, errorMsg: '' });
	const [referencia, setReferencia] = useState<any>(address.rfc);
	const initialExecuteRef = useRef<boolean>(true);
	const { data: solemtiAddress, isLoading: isValidatingZipCode } = useZipCode(address.zipCode);

	useEffect(() => {
		if (solemtiAddress?.length > 0) {
			const solemtiAddressData = solemtiAddress[0];
			handleChange({
				target: {
					name: 'country',
					value: 'Mexico',
				},
			} as ChangeEvent<HTMLInputElement>);

			handleChange({
				target: {
					name: 'state',
					value: solemtiAddressData.Estado,
				},
			} as ChangeEvent<HTMLInputElement>);

			const city = solemtiAddressData.Municipio ?? solemtiAddressData.Ciudad ?? 'Unknown';

			handleChange({
				target: {
					name: 'city',
					value: city,
				},
			} as ChangeEvent<HTMLInputElement>);

			setAddressInfo({
				city,
				state: solemtiAddressData.Estado,
				neighborhood: solemtiAddress.map((item) => item.Asentamiento),
			});
		}
	}, [solemtiAddress]);

	useEffect(() => {
		const execute = async () => {
			if (address.zipCode && address.zipCode.length === 5) {
				const respExtZone = await validateExtendedZone(address.zipCode, cart?.orderId);
				setIsExtendedZoneShipping(respExtZone?.shippingWithExtendedZone ? true : false);
			}
		};

		execute();
	}, [address.zipCode, cart?.orderId]);

	const neighborhoodWithoutAccents = useMemo(
		() =>
			(addressInfo.neighborhood || []).map((nh: string) =>
				nh.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
			),
		[addressInfo.neighborhood]
	);

	useEffect(() => {
		if (
			initialExecuteRef.current &&
			address.addressLine2 &&
			neighborhoodWithoutAccents.length > 0 &&
			!neighborhoodWithoutAccents.includes(address.addressLine2)
		) {
			initialExecuteRef.current = false;
			setCustomNeighborhood(true);
		}
	}, [address, neighborhoodWithoutAccents]);

	// Cada que cambia el zipcode
	useEffect(() => {
		if (address.zipCode && address.zipCode.length === 5) {
			setCustomNeighborhood(false);
			setOpenSelect(false);
		} else if (address.zipCode.length < 5) {
			selectChange({
				target: {
					name: 'addressLine2',
					value: '',
				},
			} as ChangeEvent<HTMLInputElement>);
		}
	}, [address.zipCode, selectChange]);

	const handleZipCodeChange = (event: ChangeEvent<HTMLInputElement>) => {
		const zipCode = event.target.value || '';

		if (/^\d*$/.test(zipCode) && zipCode.length <= 5) {
			handleChange(event);
		}
	};

	const handleNeighborhoodChange = (event: SelectChangeEvent) => {
		if (event.target.value === 'custom') {
			setCustomNeighborhood(true);

			selectChange({
				target: {
					name: 'addressLine2',
					value: '',
				},
			} as ChangeEvent<HTMLInputElement>);
		} else {
			selectChange(event);
		}
	};

	const handleBackToSelect = () => {
		setCustomNeighborhood(false);
		handleChange({
			target: {
				name: 'addressLine2',
				value: '',
			},
		} as ChangeEvent<HTMLInputElement>);
		setOpenSelect(true);
	};

	const validateName = (name: string) => {
		const namePattern = /^[A-Za-zÁÉÍÓÚáéíóúñÑ\s]+$/;
		return name?.trim() !== '' && namePattern.test(name);
	};

	const validateStreet = (name: string) => {
		const namePattern = /^[A-Za-zÁÉÍÓÚáéíóúñÑ0-9\s]+$/;
		return name?.trim() !== '' && namePattern.test(name);
	};

	const validatePhoneNumber = (phone: string) => {
		const phonePattern = /^\d{10}$/;
		return phone?.trim() !== '' && phonePattern.test(phone);
	};

	const validateEmail = (email: string) => {
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return email?.trim() !== '' && emailPattern.test(email);
	};

	const handleReferencia = (event: any) => {
		let valueSinAcentos = event.target.value.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
		valueSinAcentos = cleanTextFomEmojis(valueSinAcentos);
		setReferencia(valueSinAcentos);
		event.target.value = valueSinAcentos;
		handleChange(event);
	};

	useEffect(() => {
		if (editPerson) {
			if (
				!validatePhoneNumber(address.phone1.replace(/\s/g, '')) ||
				!validateEmail(address.email1) ||
				!validateName(address.firstName) ||
				!validateName(address.lastName) ||
				!validateName(address.lastNameMat)
			) {
				setSubmitting(true);
			} else {
				setSubmitting(false);
			}
		} else {
			if (
				!address.nickName ||
				!validateStreet(address.addressLine1) ||
				!validateXaddrExt(address.xaddr_numExt as any) ||
				!validateZipCode(address.zipCode) ||
				address.zipCode.length < 5 ||
				!validateName(address.lastName) ||
				!address.addressLine1 ||
				!address.addressLine2 ||
				(customNeighborhood && !address.addressLine2)
			) {
				setSubmitting(true);
			} else {
				setSubmitting(false);
			}
		}
	}, [address, setSubmitting]);

	const validateXaddrExt = (xaddrExt: string) => {
		const xaddrExtPattern = /^[0-9]{1,10}$/;
		return xaddrExt?.trim() !== '' && xaddrExtPattern.test(xaddrExt);
	};

	// const validateXaddrInt = (xaddrExt: string) => {
	// 	const xaddrExtPattern = /^[a-zA-Z0-9]{1,10}$/;
	// 	return xaddrExtPattern.test(xaddrExt);
	// };

	const validateZipCode = (zipCode: string) => {
		const zipCodePattern = /^[0-9]{1,5}$/;
		return zipCode?.trim() !== '' && zipCodePattern.test(zipCode);
	};

	const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		const name = event.target.name;
		const valor = event.target.value;

		switch (name) {
			case 'firstName':
				if (valor.trim() === '') {
					setErrorFirstName({
						error: true,
						errorMsg: 'Campo obligatorio',
					});
				} else {
					setErrorFirstName({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'lastName':
				if (valor.trim() === '') {
					setErrorLastName({
						error: true,
						errorMsg: 'Campo obligatorio',
					});
				} else {
					setErrorLastName({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'lastNameMat':
				if (valor.trim() === '') {
					setErrorLastNameMat({
						error: true,
						errorMsg: 'Campo obligatorio',
					});
				} else {
					setErrorLastNameMat({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'phone1':
				if (!validatePhoneNumber(valor.replace(/\s/g, ''))) {
					setErrorPhone1({
						error: true,
						errorMsg: 'El teléfono debe tener 10 dígitos',
					});
				} else {
					setErrorPhone1({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'email1':
				if (!validateEmail(valor)) {
					setErrorEmail1({
						error: true,
						errorMsg: 'Capturar formato correcto',
					});
				} else {
					setErrorEmail1({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'nickName':
				if (valor === '') {
					setErrorNickName({
						error: true,
						errorMsg: 'Campo obligatorio',
					});
				} else {
					setErrorNickName({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'addressLine1':
				if (!validateStreet(valor)) {
					setErrorAddressLine1({
						error: true,
						errorMsg: 'Campo obligatorio',
					});
				} else {
					setErrorAddressLine1({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'xaddr_numExt':
				if (!validateXaddrExt(valor)) {
					setErrorXaddr_numExt({
						error: true,
						errorMsg: 'Campo obligatorio',
					});
				} else {
					setErrorXaddr_numExt({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'zipCode':
				if (!validateZipCode(valor) || valor.length < 5) {
					setErrorZipCode({
						error: true,
						errorMsg: 'Capturar 5 dígitos',
					});
				} else {
					setErrorZipCode({
						error: false,
						errorMsg: '',
					});
				}
				break;

			case 'addressLine2':
				if (valor === '') {
					setErrorAddressLine2({
						error: true,
						errorMsg: 'Campo obligatorio',
					});
				} else {
					setErrorAddressLine2({
						error: false,
						errorMsg: '',
					});
				}
				break;

			default:
				break;
		}
	};

	const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		const name = event.target.name;

		switch (name) {
			case 'firstName':
				setErrorFirstName({
					error: false,
					errorMsg: '',
				});
				break;

			case 'lastName':
				setErrorLastName({
					error: false,
					errorMsg: '',
				});
				break;

			case 'lastNameMat':
				setErrorLastNameMat({
					error: false,
					errorMsg: '',
				});
				break;

			case 'phone1':
				setErrorPhone1({
					error: false,
					errorMsg: '',
				});
				break;

			case 'email1':
				setErrorEmail1({
					error: false,
					errorMsg: '',
				});
				break;

			case 'nickName':
				setErrorNickName({
					error: false,
					errorMsg: '',
				});
				break;

			case 'addressLine1':
				setErrorAddressLine1({
					error: false,
					errorMsg: '',
				});
				break;

			case 'xaddr_numExt':
				setErrorXaddr_numExt({
					error: false,
					errorMsg: '',
				});
				break;

			case 'zipCode':
				setErrorZipCode({
					error: false,
					errorMsg: '',
				});
				break;

			case 'addressLine2':
				setErrorAddressLine2({
					error: false,
					errorMsg: '',
				});
				break;

			case undefined:
				setErrorAddressLine2({
					error: false,
					errorMsg: '',
				});
				break;

			default:
				break;
		}
	};

	return (
		<Stack sx={addressFormFieldsSX}>
			{editPerson ? (
				<Fragment>
					{/* {formLabel} */}
					<Grid item container spacing={3} sm={12} paddingTop={'18px'}>
						<Grid item xs={12} sm={6}>
							<TextField
								id="address-form-firstName"
								data-testid="address-form-firstName"
								name="firstName"
								label={addressFormNLS.Labels.FirstName.t()}
								onChange={(event) => {
									const value = event.target.value;
									if (value === '' || validateName(value)) {
										handleChange(event as any);
									}
								}}
								onFocus={handleOnFocus}
								onBlur={handleOnBlur}
								error={errorFirstName.error}
								helperText={errorFirstName.errorMsg}
								value={address.firstName}
								inputProps={{ maxLength: 40 }}
								fullWidth
								autoFocus={nickNameDisabled}
								autoComplete="given-name"
								required
								sx={addressFormFieldsSX}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								required
								id="address-form-lastName"
								data-testid="address-form-lastName"
								name="lastName"
								label={'Apellido paterno'}
								onChange={(event) => {
									const value = event.target.value;
									if (value === '' || validateName(value)) {
										handleChange(event as any);
									}
								}}
								onFocus={handleOnFocus}
								onBlur={handleOnBlur}
								error={errorLastName.error}
								helperText={errorLastName.errorMsg}
								value={address.lastName}
								inputProps={{ maxLength: 40 }}
								fullWidth
								autoComplete="family-name"
								sx={addressFormFieldsSX}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								required
								id="address-form-lastNameMat"
								data-testid="address-form-lastNameMat"
								name="lastNameMat"
								label={'Apellido materno'}
								onChange={(event) => {
									const value = event.target.value;
									if (value === '' || validateName(value)) {
										handleChange(event as any);
									}
								}}
								onFocus={handleOnFocus}
								onBlur={handleOnBlur}
								error={errorLastNameMat.error}
								helperText={errorLastNameMat.errorMsg}
								value={address.lastNameMat}
								inputProps={{ maxLength: 40 }}
								fullWidth
								autoComplete="family-name"
								sx={addressFormFieldsSX}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								id="address-form-phone1"
								name="phone1"
								type="tel"
								data-testid="address-form-phone1"
								label={'Teléfono'}
								// onChange={(event) => {
								// 	const value = event.target.value;
								// 	if (/^\d*$/.test(value) && value.length <= 10) {
								// 		handleChange(event as any);
								// 	}
								// }}
								onChange={(event: ChangeEvent<HTMLInputElement>) =>
									formatPhoneNumber(event, handleChange)
								}
								onFocus={handleOnFocus}
								onBlur={handleOnBlur}
								error={errorPhone1.error}
								helperText={errorPhone1.errorMsg}
								value={formatPhoneNumberValue(address.phone1)}
								inputProps={{ maxLength: 32, pattern: REG_EX.PHONE.source }}
								fullWidth
								autoComplete="tel"
								InputProps={{
									endAdornment: (
										<Tooltip
											title={
												<Typography sx={{ fontSize: 'var(--font-size-caption)' }}>
													{'En caso de que tengamos que'} <br />
													{'contactarte sobre tu pedido'}
												</Typography>
											}
											placement="top-end"
											arrow
										>
											<ErrorIcon sx={{ fontSize: 'var(--font-size-body)', color: '#C9C9C9' }} />
										</Tooltip>
									),
								}}
								sx={addressFormFieldsSX}
							/>
						</Grid>
						<Grid item xs={12}>
							<TextField
								required
								id="address-form-email"
								data-testid="address-form-email"
								name="email1"
								type="email"
								label={addressFormNLS.Labels.Email.t()}
								onChange={handleChange}
								value={address.email1}
								onFocus={handleOnFocus}
								onBlur={handleOnBlur}
								error={errorEmail1.error}
								helperText={errorEmail1.errorMsg}
								inputProps={{ maxLength: 35, pattern: REG_EX.EMAIL.source }}
								fullWidth
								autoComplete="email"
								sx={addressFormFieldsSX}
							/>
							<FormControlLabel
								sx={{ marginLeft: '0px' }}
								control={
									<BpCheckbox
										data-testid={`schedule-recurring-checkbox`}
										id={`schedule-recurring-checkbox`}
										name={`receiveEmail`}
										checked={address.receiveEmail}
										onChange={handleChange}
										sx={AccountInformationBpCheckboxSX}
									/>
								}
								label={
									<Typography sx={AccountInformationFormCheckboxSX}>
										{
											'Deseo recibir promociones y noticias en mi correo electrónico por parte de dportenis.'
										}
									</Typography>
								}
							/>
						</Grid>
					</Grid>
				</Fragment>
			) : (
				<Grid item container spacing={3} sm={12}>
					<Grid
						paddingLeft={'var(--spacing-6)'}
						paddingTop={addressBook ? '' : 'var(--spacing-6)'}
						xs={12}
						display={'flex'}
						flexDirection={'row'}
						justifyContent={'space-between'}
						alignItems={'center'}
					>
						{address.primary ? (
							<Typography sx={addressBook ? shippingTitleBookSX : shippingTitleGeneralSX}>
								{'DIRECCIÓN DE ENVÍO'}
							</Typography>
						) : (
							<Typography sx={addressBook ? shippingTitleBookSX : shippingTitleGeneralSX}>
								{'NUEVA DIRECCIÓN'}
							</Typography>
						)}
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							data-testid="address-form-nickName"
							id="address-form-nickName"
							name="nickName"
							label={'Destinatario (ej. Mi oficina, mi casa...)'}
							onChange={
								address.primary === 'true'
									? handleChange
									: (event) => {
											const value = event.target.value;
											if (value === '' || validateName(value)) {
												handleChange(event as any);
											}
									  }
							}
							value={address.nickName}
							inputProps={{
								required: true,
								maxLength: 128,
								pattern: REG_EX.NICKNAME_ALPHA_NUMERIC_SPECIAL_CHAR.source,
							}}
							onFocus={handleOnFocus}
							onBlur={handleOnBlur}
							error={errorNickname.error}
							helperText={errorNickname.errorMsg}
							fullWidth
							autoComplete="nickname"
							required
							autoFocus={address.primary ? true : false}
							disabled={nickNameDisabled}
							sx={addressFormFieldsSX}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							data-testid="address-form-address-line1"
							id="address-form-address-line1"
							name="addressLine1"
							label={'Calle'}
							onChange={(event) => {
								const value = event.target.value;
								if (value === '' || (validateStreet(value) && value.length <= 50)) {
									handleChange(event as any);
								}
							}}
							onFocus={handleOnFocus}
							onBlur={handleOnBlur}
							error={errorAddressLine1.error}
							helperText={errorAddressLine1.errorMsg}
							value={address.addressLine1}
							inputProps={{ maxLength: 99 }}
							fullWidth
							autoComplete="address-line1"
							// autoFocus={nickNameDisabled}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							data-testid="address-form-address-number-ext"
							id="address-form-address-number-ext"
							name="xaddr_numExt"
							label={'Número exterior'}
							onChange={(event) => {
								const value = event.target.value;
								if (value === '' || (validateXaddrExt(value) && value.length <= 10)) {
									handleChange(event as any);
								}
							}}
							onFocus={(event) => {
								const value = event.target.value;
								if (value === '' || (validateXaddrExt(value) && value.length <= 10)) {
									handleChange(event as any);
								}
							}}
							onBlur={handleOnBlur}
							error={errorXaddr_numExt.error}
							helperText={errorXaddr_numExt.errorMsg}
							value={address.xaddr_numExt}
							inputProps={{ maxLength: 99 }}
							fullWidth
							autoComplete="number-ext"
							sx={addressFormFieldsSX}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							// required
							data-testid="address-form-address-number-int"
							id="address-form-address-number-int"
							name="xaddr_numInt"
							label={'Número interior'}
							onChange={(event) => {
								const value = event.target.value;
								if (value === '' || (validateStreet(value) && value.length <= 10)) {
									handleChange(event as any);
								}
							}}
							onFocus={(event) => {
								const value = event.target.value;
								if (value === '' || (validateStreet(value) && value.length <= 10)) {
									handleChange(event as any);
								}
							}}
							// error={error.xaddr_numInt}
							value={address.xaddr_numInt}
							inputProps={{ maxLength: 99 }}
							fullWidth
							autoComplete="number-int"
							sx={addressFormFieldsSX}
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							id="address-form-zipCode"
							name="zipCode"
							data-testid="address-form-zipCode"
							label={addressFormNLS.Labels.ZipCode.t()}
							onChange={handleZipCodeChange}
							onFocus={handleOnFocus}
							onBlur={handleOnBlur}
							error={errorZipCode.error}
							helperText={errorZipCode.errorMsg}
							value={address.zipCode}
							inputProps={{ maxLength: 5, inputMode: 'numeric', pattern: '[0-9]*' }}
							fullWidth
							autoComplete="postal-code"
							sx={addressFormFieldsSX}
							disabled={isValidatingZipCode}
						/>
						{isValidatingZipCode ? (
							<Grid
								style={{
									display: 'flex',
									alignItems: 'center',
									columnGap: '5px',
									marginTop: '3px',
								}}
							>
								<CircularProgress size={14} thickness={6} style={{ color: '#b5beca' }} />
								<Typography style={{ color: '#b5beca', fontSize: 'var(--font-size-caption)' }}>
									{'Validando código postal...'}
								</Typography>
							</Grid>
						) : (
							isExtendedZoneShipping && <AlertExtendedZone inAddressForm={true} />
						)}
					</Grid>
					<Grid item xs={12} sm={6}>
						{isValidatingZipCode ? (
							<Skeleton
								variant="rounded"
								width="100%"
								height="55px"
								sx={{ minHeight: '55px', height: '55px' }}
							/>
						) : (
							<FormControl fullWidth required variant="outlined">
								<InputLabel
									sx={{
										backgroundColor: '#FFF',
										padding: '0 10px 0 5px',
										color: errorAddressLine2.error
											? '#d32f2f'
											: address.zipCode.length !== 5
											? '#b5beca'
											: '',
									}}
									id="address-form-address-line2-label"
								>
									{customNeighborhood ? '' : 'Colonia'}
								</InputLabel>

								{customNeighborhood ? (
									<TextField
										required
										data-testid="address-form-address-line2"
										id="address-form-address-line2"
										name="addressLine2"
										label={'Escribe tu colonia'}
										onChange={(event) => {
											const value = event.target.value;
											if (value === '' || (validateStreet(value) && value.length <= 50)) {
												handleChange(event as any);
											}
										}}
										autoFocus={address.addressLine2 === EMPTY_STRING}
										onFocus={handleOnFocus}
										onBlur={handleOnBlur}
										error={errorAddressLine2.error}
										helperText={errorAddressLine2.errorMsg}
										value={address.addressLine2}
										inputProps={{ maxLength: 99 }}
										fullWidth
										autoComplete="address-line2"
										InputProps={{
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														aria-label="volver a seleccionar"
														onClick={handleBackToSelect}
														edge="end"
													>
														<CustomIconComponent />
													</IconButton>
												</InputAdornment>
											),
										}}
										sx={addressFormFieldsSX}
									/>
								) : null}
								{!customNeighborhood ? (
									<>
										<Select
											labelId="address-form-address-line2-label"
											id="address-form-address-line2"
											name="addressLine2"
											value={address.addressLine2}
											onChange={handleNeighborhoodChange}
											onFocus={handleOnFocus}
											onBlur={handleOnBlur}
											error={errorAddressLine2.error}
											// helperText={errorAddressLine2.errorMsg}

											open={openSelect}
											onOpen={() => setOpenSelect(true)}
											onClose={() => setOpenSelect(false)}
											defaultOpen={false}
											data-testid="address-form-address-line2"
											disabled={address.zipCode.length !== 5}
											IconComponent={CustomIconComponent}
										>
											{neighborhoodWithoutAccents
												? neighborhoodWithoutAccents.map((nh) => (
														<MenuItem key={nh} value={nh}>
															{nh}
														</MenuItem>
												  ))
												: null}
											{address.zipCode ? (
												<MenuItem value="custom">{'Mi colonia no aparece'}</MenuItem>
											) : null}
										</Select>
										{!!errorAddressLine2.error ? (
											<FormHelperText sx={{ color: '#d32f2f' }}>
												{errorAddressLine2.errorMsg}
											</FormHelperText>
										) : null}
									</>
								) : null}
							</FormControl>
						)}
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							data-testid="state-textfield"
							id="state-textfield"
							name="state"
							// error={error.state}
							label={'Estado o provincia'}
							autoComplete="address-level1"
							onChange={handleChange}
							value={address.state}
							inputProps={{ maxLength: 40 }}
							fullWidth
							disabled
						/>
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField
							required
							id="address-form-city"
							data-testid="address-form-city"
							name="city"
							// error={error.city}
							label={'Ciudad o municipio'}
							onChange={handleChange}
							value={address.city}
							inputProps={{ maxLength: 40 }}
							fullWidth
							autoComplete="address-level2"
							disabled
							// helperText={helperText.city}
						/>
					</Grid>
					<Grid item xs={12} sm={12}>
						<TextField
							id="address-form-reference"
							data-testid="address-form-reference"
							name="rfc"
							// error={error.city}
							label={'Referencia'}
							onChange={handleReferencia}
							value={referencia}
							inputProps={{ maxLength: 60 }}
							fullWidth
							// helperText={helperText.city}
						/>
					</Grid>
				</Grid>
			)}
		</Stack>
	);
};
